/**
 * Intersection Observer for scroll animations
 * Works in both React and static HTML
 */

// Animation selectors
const ANIMATION_SELECTORS = [
  '.animate-on-scroll',
  '.fade-in',
  '.fade-in-up',
  '.fade-in-down',
  '.fade-in-left',
  '.fade-in-right',
  '.scale-up',
  '.zoom-in',
  '.stagger-children',
];

// Default options
const DEFAULT_OPTIONS = {
  threshold: 0.1,       // Trigger when 10% visible
  rootMargin: '0px 0px -50px 0px', // Trigger slightly before element enters viewport
};

/**
 * Initialize scroll animations
 * Call this once when DOM is ready
 */
export function initScrollAnimations(options = {}) {
  const config = { ...DEFAULT_OPTIONS, ...options };

  // Check for IntersectionObserver support
  if (!('IntersectionObserver' in window)) {
    // Fallback: show all elements immediately
    document.querySelectorAll(ANIMATION_SELECTORS.join(',')).forEach(el => {
      el.classList.add('is-visible');
    });
    return;
  }

  // Create observer
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        // Optionally unobserve after animation (better performance)
        if (!config.repeat) {
          observer.unobserve(entry.target);
        }
      } else if (config.repeat) {
        entry.target.classList.remove('is-visible');
      }
    });
  }, {
    threshold: config.threshold,
    rootMargin: config.rootMargin,
  });

  // Observe all animated elements
  document.querySelectorAll(ANIMATION_SELECTORS.join(',')).forEach(el => {
    observer.observe(el);
  });

  return observer;
}

/**
 * React hook for scroll animations
 * Use in App.jsx or layout component
 */
export function useScrollAnimations(options = {}) {
  if (typeof window === 'undefined') return;

  // Use React's useEffect if available, otherwise run directly
  if (typeof require !== 'undefined') {
    try {
      const { useEffect } = require('react');
      useEffect(() => {
        const observer = initScrollAnimations(options);
        return () => observer?.disconnect();
      }, []);
    } catch {
      // Not in React context, initialize directly
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => initScrollAnimations(options));
      } else {
        initScrollAnimations(options);
      }
    }
  }
}

/**
 * Get script for static HTML
 * Include this in the generated HTML
 */
export function getAnimationScript() {
  return `
<script>
(function() {
  var selectors = '${ANIMATION_SELECTORS.join(',')}';
  var options = { threshold: 0.1, rootMargin: '0px 0px -50px 0px' };

  if (!('IntersectionObserver' in window)) {
    document.querySelectorAll(selectors).forEach(function(el) {
      el.classList.add('is-visible');
    });
    return;
  }

  var observer = new IntersectionObserver(function(entries) {
    entries.forEach(function(entry) {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, options);

  document.querySelectorAll(selectors).forEach(function(el) {
    observer.observe(el);
  });
})();
</script>`;
}

/**
 * Get animation CSS as string
 * For inline styles in generated HTML
 */
export function getAnimationCSS() {
  return `
<style>
.animate-on-scroll,.fade-in,.fade-in-up,.fade-in-down,.fade-in-left,.fade-in-right,.scale-up,.zoom-in{opacity:0;transition:opacity .6s ease-out,transform .6s ease-out}
.fade-in-up{transform:translateY(30px)}
.fade-in-down{transform:translateY(-30px)}
.fade-in-left{transform:translateX(-30px)}
.fade-in-right{transform:translateX(30px)}
.scale-up{transform:scale(.9)}
.zoom-in{transform:scale(.5)}
.is-visible{opacity:1!important;transform:none!important}
.stagger-children>*{opacity:0;transform:translateY(20px);transition:opacity .5s ease-out,transform .5s ease-out}
.stagger-children.is-visible>*{opacity:1;transform:translateY(0)}
.stagger-children.is-visible>*:nth-child(1){transition-delay:0ms}
.stagger-children.is-visible>*:nth-child(2){transition-delay:100ms}
.stagger-children.is-visible>*:nth-child(3){transition-delay:200ms}
.stagger-children.is-visible>*:nth-child(4){transition-delay:300ms}
.stagger-children.is-visible>*:nth-child(5){transition-delay:400ms}
.stagger-children.is-visible>*:nth-child(6){transition-delay:500ms}
.hover-lift{transition:transform .3s ease,box-shadow .3s ease}
.hover-lift:hover{transform:translateY(-4px);box-shadow:0 10px 40px rgba(0,0,0,.1)}
@media(prefers-reduced-motion:reduce){.animate-on-scroll,.fade-in,.fade-in-up,.fade-in-down,.fade-in-left,.fade-in-right,.scale-up,.zoom-in,.stagger-children>*{opacity:1!important;transform:none!important;transition:none!important}}
</style>`;
}

export default initScrollAnimations;
